export class ErrorMessages {
  static apiError: string =
    "Request to API failed. Check if url is valid and try again.";
  static unexpected: string = "Unexpected error.";
  static badRequest: string = "Bad request.";
  static badCredentials: string = "User with given credentials not found.";
  static unauthorized: string = "Unauthorized. Please login.";
  static emailNotFound: string = "Email not found.";
  static jsonParseFailed: string =
    "Failed to parse/convert json to data model.";
}

export const catchErrorMessage = (e: unknown) => {
  if (e instanceof Error) {
    return e.message;
  } else if (typeof e === "string") {
    return e;
  }
  return "unknown error.";
};

const TOP_LEVEL_MESSAGE_KEYS = new Set([
  "error",
  "error_code",
  "detail",
  "message",
  "non_field_errors",
]);

/**
 * First message under a DRF field key, two levels deep at most: a string,
 * a list of strings (joined), or an object keyed by row index whose values
 * are either, e.g. `{"options": ["..."]}`, `{"installment_plans": "..."}` or
 * `{"installment_plans": {"1": "..."}}`.
 */
export const firstFieldMessage = (
  value: unknown,
  depth = 0
): string | undefined => {
  if (typeof value === "string") return value;
  if (depth >= 2 || !value || typeof value !== "object") return undefined;
  if (Array.isArray(value)) {
    const strings = value.filter((item) => typeof item === "string");
    if (strings.length) return strings.join(", ");
  }
  return Object.values(value)
    .map((nested) => firstFieldMessage(nested, depth + 1))
    .find(Boolean);
};

/**
 * Extracts a user-friendly error message from API errors.
 * Handles the common error response shapes from the TFA API.
 */
export const extractApiErrorMessage = (
  error: unknown,
  fallbackMessage: string = "An error occurred"
): string => {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === "string") {
    return error;
  }

  if (!error) {
    return fallbackMessage;
  }

  const err = error as any;
  const errorData = err.response?.data || err.data;

  if (!errorData) {
    return err.message || fallbackMessage;
  }

  if (typeof errorData === "string") {
    return errorData;
  }

  // Check common error fields
  if (errorData.detail) return String(errorData.detail);
  if (errorData.error) return String(errorData.error);
  if (errorData.message) return String(errorData.message);

  if (errorData.non_field_errors) {
    return Array.isArray(errorData.non_field_errors)
      ? errorData.non_field_errors.join(", ")
      : String(errorData.non_field_errors);
  }

  const fieldMessage = Object.entries(errorData)
    .filter(([key]) => !TOP_LEVEL_MESSAGE_KEYS.has(key))
    .map(([, value]) => firstFieldMessage(value, 1))
    .find(Boolean);
  if (fieldMessage) return fieldMessage;

  return fallbackMessage;
};

/**
 * Reads the machine-readable `error_code` a coded 400 body carries next to
 * its `error` text, so callers can branch on it instead of the message.
 */
export const extractApiErrorCode = (error: unknown): string | undefined => {
  if (!error || typeof error !== "object") return undefined;
  const err = error as any;
  const code = (err.response?.data ?? err.data)?.error_code;
  return typeof code === "string" ? code : undefined;
};

/**
 * The per-field entries of a DRF 400 body (everything but the top-level
 * message keys), so a caller can place each next to its row. Undefined when
 * the body carries none.
 */
export const extractApiFieldErrors = (
  error: unknown
): Record<string, unknown> | undefined => {
  if (!error || typeof error !== "object") return undefined;
  const err = error as any;
  const body = err.response?.data ?? err.data;
  if (!body || typeof body !== "object" || Array.isArray(body)) {
    return undefined;
  }
  const fields = Object.fromEntries(
    Object.entries(body).filter(([key]) => !TOP_LEVEL_MESSAGE_KEYS.has(key))
  );
  return Object.keys(fields).length ? fields : undefined;
};

export class UnexpectedError extends Error {
  constructor(message?: string) {
    super(message ? message : ErrorMessages.unexpected);
    Object.setPrototypeOf(this, UnexpectedError.prototype);
  }
}

export class BadRequestError extends Error {
  constructor(message?: string) {
    super(message ? message : ErrorMessages.badRequest);
    Object.setPrototypeOf(this, BadRequestError.prototype);
  }
}

export class BadAuthCredentialsError extends Error {
  constructor(message?: string) {
    super(message ? message : ErrorMessages.badCredentials);
    Object.setPrototypeOf(this, BadAuthCredentialsError.prototype);
  }
}

export class UnauthorizedError extends Error {
  constructor(message?: string) {
    super(message ? message : ErrorMessages.unauthorized);
    Object.setPrototypeOf(this, BadAuthCredentialsError.prototype);
  }
}

export class JsonParsingError extends Error {
  constructor(message?: string) {
    super(message ? message : ErrorMessages.jsonParseFailed);
    Object.setPrototypeOf(this, JsonParsingError.prototype);
  }
}

export class NotFoundError extends Error {
  constructor(message: string) {
    super(message);
    Object.setPrototypeOf(this, NotFoundError.prototype);
  }
}
